import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useChat } from '../../contexts/ChatContext';
import { useSessionUser } from '../../SessionContext';

const getAgentDisplayLabel = (agent = {}) => {
  const extension = String(agent.extension || agent.ext || '').trim();
  const primaryName = String(
    agent.displayName ||
    agent.fullName ||
    agent.name ||
    agent.username ||
    ''
  ).trim();

  if (primaryName && extension) {
    return `${primaryName} (${extension})`;
  }

  return primaryName || extension || 'Agent';
};

const getAgentInitials = (agent = {}) => {
  const label = getAgentDisplayLabel(agent).replace(/\(\d+\)\s*$/, '').trim();
  const parts = label.split(/\s+/).filter(Boolean).slice(0, 2);
  if (!parts.length) return 'AG';
  return parts.map((part) => part[0].toUpperCase()).join('');
};

const getAgentName = (agent = {}) =>
  String(
    agent.displayName ||
    agent.fullName ||
    agent.name ||
    agent.username ||
    ''
  ).trim();

const getAgentExtension = (agent = {}) =>
  String(agent.extension || agent.ext || '').trim();

const ChatWindow = ({ sessionId, targetAgent, onClose }) => {
  const {
    messages,
    sendMessage,
    typingUsers,
    loadChatHistory,
    joinChat,
    leaveChat,
    markAsRead,
    emitTypingStart,
    emitTypingStop,
    deleteMessageForMe,
    deleteMessageForEveryone,
    clearChatForMe
  } = useChat();
  const { user } = useSessionUser();
  const [inputMessage, setInputMessage] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [isScrolledUp, setIsScrolledUp] = useState(false);
  const [activeMenuMessageId, setActiveMenuMessageId] = useState(null);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const typingTimeoutRef = useRef(null);

  const currentExtension = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem('extension') || '{}');
    } catch {
      return {};
    }
  }, []);
  const currentAgentId = String(
    currentExtension.extensionNumber ||
    currentExtension.extension ||
    user?.userId ||
    ''
  ).trim();

  const sessionMessages = useMemo(() => messages[sessionId] || [], [messages, sessionId]);
  const typingUsersList = useMemo(
    () => (typingUsers[sessionId] || []).filter((id) => id !== currentAgentId),
    [currentAgentId, sessionId, typingUsers]
  );
  const agentName = getAgentName(targetAgent);
  const agentExtension = getAgentExtension(targetAgent);
  const agentLabel = getAgentDisplayLabel(targetAgent);

  useEffect(() => {
    if (!sessionId) return undefined;

    joinChat(sessionId);
    loadChatHistory(sessionId, currentAgentId).catch((error) => {
      console.error('Failed to load chat history:', error);
    });

    return () => {
      leaveChat();
    };
  }, [currentAgentId, joinChat, leaveChat, loadChatHistory, sessionId]);

  useEffect(() => {
    if (!sessionId || !currentAgentId) return;
    markAsRead(sessionId, currentAgentId).catch((error) => {
      console.error('Failed to mark chat as read:', error);
    });
  }, [sessionId, currentAgentId, markAsRead]);

  useEffect(() => {
    if (!isScrolledUp) {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [isScrolledUp, sessionMessages]);

  useEffect(() => () => {
    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
    }
  }, []);

  const handleScroll = useCallback((e) => {
    const { scrollTop, scrollHeight, clientHeight } = e.target;
    setIsScrolledUp(scrollHeight - scrollTop - clientHeight >= 20);
  }, []);

  const handleSendMessage = useCallback(async () => {
    if (!inputMessage.trim() || !currentAgentId || !sessionId) return;

    await sendMessage(sessionId, inputMessage.trim(), 'agent', currentAgentId, agentExtension);
    setInputMessage('');

    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
    }
    setIsTyping(false);
    emitTypingStop(sessionId, 'agent', currentAgentId);
  }, [agentExtension, currentAgentId, emitTypingStop, inputMessage, sendMessage, sessionId]);

  const handleKeyPress = useCallback((e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage().catch((error) => {
        console.error('Send message failed:', error);
      });
    }
  }, [handleSendMessage]);

  const handleInputChange = useCallback((e) => {
    const value = e.target.value;
    setInputMessage(value);

    if (!currentAgentId || !sessionId) return;

    if (!isTyping) {
      setIsTyping(true);
      emitTypingStart(sessionId, 'agent', currentAgentId);
    }

    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
    }

    typingTimeoutRef.current = setTimeout(() => {
      setIsTyping(false);
      emitTypingStop(sessionId, 'agent', currentAgentId);
    }, 1000);
  }, [currentAgentId, emitTypingStart, emitTypingStop, isTyping, sessionId]);

  const handleDeleteForMe = useCallback(async (messageId) => {
    await deleteMessageForMe(sessionId, messageId, currentAgentId);
    setActiveMenuMessageId(null);
  }, [currentAgentId, deleteMessageForMe, sessionId]);

  const handleDeleteForEveryone = useCallback(async (messageId) => {
    await deleteMessageForEveryone(sessionId, messageId, currentAgentId);
    setActiveMenuMessageId(null);
  }, [currentAgentId, deleteMessageForEveryone, sessionId]);

  const handleClearChat = useCallback(async () => {
    if (!window.confirm('Clear this chat for your account?')) return;
    await clearChatForMe(sessionId, currentAgentId);
  }, [clearChatForMe, currentAgentId, sessionId]);

  return (
    <div
      className="chat-window d-flex flex-column h-100"
      style={{
        minHeight: '400px',
        background: 'linear-gradient(180deg, #f8fbff 0%, #f3f6fb 100%)'
      }}
    >
      <div
        className="chat-header d-flex justify-content-between align-items-center gap-3 px-3 px-sm-4 py-3"
        style={{
          borderBottom: '1px solid #dce4ee',
          background: 'rgba(255,255,255,0.94)',
          backdropFilter: 'blur(10px)'
        }}
      >
        <div className="d-flex align-items-center gap-3 flex-grow-1" style={{ minWidth: 0, overflow: 'hidden' }}>
          <div
            className="d-inline-flex align-items-center justify-content-center flex-shrink-0"
            style={{
              width: '44px',
              height: '44px',
              borderRadius: '14px',
              background: 'linear-gradient(135deg, #0f766e 0%, #2563eb 100%)',
              color: '#fff',
              fontWeight: 700,
              fontSize: '0.95rem',
              letterSpacing: '0.04em',
              boxShadow: '0 10px 20px rgba(37,99,235,0.16)'
            }}
          >
            {getAgentInitials(targetAgent)}
          </div>
          <div style={{ minWidth: 0, flex: 1, overflow: 'hidden' }}>
            <div
              className="d-flex align-items-center gap-2"
              style={{ marginBottom: '4px', minWidth: 0 }}
            >
              <div
                className="fw-semibold text-dark"
                style={{
                  fontSize: '1rem',
                  lineHeight: 1.2,
                  minWidth: 0,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap'
                }}
              >
                {agentName || agentLabel}
              </div>
              {agentExtension && (
                <span
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    padding: '4px 10px',
                    borderRadius: '999px',
                    fontSize: '0.72rem',
                    fontWeight: 700,
                    color: '#175cd3',
                    background: '#eff6ff',
                    border: '1px solid #bfdbfe',
                    whiteSpace: 'nowrap'
                  }}
                >
                  Ext {agentExtension}
                </span>
              )}
            </div>
            <div
              className="d-flex align-items-center gap-2 mt-2"
              style={{
                minWidth: 0,
                flexWrap: 'nowrap'
              }}
            >
            </div>
          </div>
        </div>

        <div
          className="d-flex align-items-center justify-content-end gap-2 flex-shrink-0"
          style={{ marginLeft: 'auto' }}
        >
          <button
            type="button"
            className="btn btn-sm d-inline-flex align-items-center gap-2"
            onClick={() => handleClearChat().catch((error) => {
              console.error('Clear chat failed:', error);
            })}
          style={{
              border: '1px solid #d0d7de',
              color: '#344054',
              backgroundColor: '#fff',
              borderRadius: '999px',
              fontSize: '0.76rem',
              fontWeight: 600,
              padding: '7px 12px',
              whiteSpace: 'nowrap',
              boxShadow: '0 4px 12px rgba(15, 23, 42, 0.04)'
            }}
          >
            <i className="bi bi-trash3" style={{ fontSize: '0.82rem', lineHeight: 1 }} />
            <span>Clear Chat</span>
          </button>
          <button
            className="btn d-inline-flex align-items-center justify-content-center p-0 border-0"
            onClick={onClose}
            aria-label="Close"
            style={{
              width: '36px',
              height: '36px',
              fontSize: '1.05rem',
              lineHeight: 1,
              borderRadius: '50%',
              background: '#f8fafc',
              color: '#667085',
              boxShadow: 'inset 0 0 0 1px #e2e8f0'
            }}
          >
            <i className="bi bi-x-lg" />
          </button>
        </div>
      </div>

      <div
        className="chat-messages flex-grow-1 overflow-auto px-3 py-4"
        style={{
          maxHeight: 'calc(100% - 154px)',
          background:
            'radial-gradient(circle at top right, rgba(37,99,235,0.06), transparent 22%), linear-gradient(180deg, #f8fbff 0%, #f4f7fb 100%)'
        }}
        onScroll={handleScroll}
      >
        {sessionMessages.map((msg, index) => {
          const senderId = String(msg.senderId || msg.SenderId || '').trim();
          const messageText = msg.messageText || msg.MessageText || msg.message || '';
          const createdAt = msg.createdAt || msg.CreatedAt || new Date().toISOString();
          const isOwn = senderId === currentAgentId;
          const messageId = msg.id || msg.Id || index;
          const menuOpen = String(activeMenuMessageId) === String(messageId);

          return (
            <div
              key={messageId}
              className={`message mb-3 d-flex ${isOwn ? 'justify-content-end' : 'justify-content-start'}`}
            >
              <div style={{ maxWidth: '78%', position: 'relative' }}>
                <div
                  className={`px-3 py-2 ${isOwn ? 'text-white' : 'text-dark'}`}
                  style={{
                    borderRadius: isOwn ? '18px 18px 6px 18px' : '18px 18px 18px 6px',
                    background: isOwn ? 'linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)' : '#ffffff',
                    border: isOwn ? 'none' : '1px solid #dbe4ee',
                    boxShadow: isOwn
                      ? '0 10px 22px rgba(37,99,235,0.18)'
                      : '0 8px 18px rgba(15,23,42,0.05)'
                  }}
                >
                  <div className="d-flex align-items-start justify-content-between gap-2">
                    <div
                      style={{
                        fontSize: '0.95rem',
                        lineHeight: 1.5,
                        whiteSpace: 'pre-wrap',
                        wordBreak: 'break-word',
                        flex: 1
                      }}
                    >
                      {messageText}
                    </div>
                    <button
                      type="button"
                      className={`btn btn-sm border-0 p-0 ${isOwn ? 'text-white-50' : 'text-muted'}`}
                      onClick={() => setActiveMenuMessageId(menuOpen ? null : messageId)}
                      style={{ fontSize: '1rem', lineHeight: 1, minWidth: '18px' }}
                    >
                      &#8942;
                    </button>
                  </div>
                  <div
                    className={isOwn ? 'text-white-50' : 'text-muted'}
                    style={{ fontSize: '0.71rem', marginTop: '7px', letterSpacing: '0.01em' }}
                  >
                    {new Date(createdAt).toLocaleTimeString()}
                  </div>
                </div>

                {menuOpen && (
                  <div
                    className="bg-white"
                    style={{
                      position: 'absolute',
                      top: '100%',
                      right: isOwn ? 0 : 'auto',
                      left: isOwn ? 'auto' : 0,
                      marginTop: '8px',
                      border: '1px solid #dbe4ee',
                      borderRadius: '14px',
                      minWidth: '170px',
                      zIndex: 3,
                      overflow: 'hidden',
                      boxShadow: '0 18px 36px rgba(15,23,42,0.14)'
                    }}
                  >
                    <button
                      type="button"
                      className="btn btn-sm w-100 text-start"
                      onClick={() => handleDeleteForMe(messageId).catch((error) => {
                        console.error('Delete for me failed:', error);
                      })}
                      style={{
                        borderRadius: 0,
                        padding: '11px 14px',
                        color: '#334155',
                        background: '#fff'
                      }}
                    >
                      Delete for me
                    </button>
                    {isOwn && (
                      <button
                        type="button"
                        className="btn btn-sm w-100 text-start"
                        onClick={() => handleDeleteForEveryone(messageId).catch((error) => {
                          console.error('Delete for everyone failed:', error);
                        })}
                        style={{
                          borderRadius: 0,
                          padding: '11px 14px',
                          color: '#b42318',
                          borderTop: '1px solid #eef2f6',
                          background: '#fff'
                        }}
                      >
                        Delete for everyone
                      </button>
                    )}
                  </div>
                )}
              </div>
            </div>
          );
        })}

        {typingUsersList.length > 0 && (
          <div className="px-2" style={{ fontSize: '0.8rem', color: '#667085' }}>
            {getAgentDisplayLabel(targetAgent)} is typing...
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      <div
        className="chat-input px-3 py-3"
        style={{
          borderTop: '1px solid #dbe4ee',
          backgroundColor: 'rgba(255,255,255,0.95)',
          backdropFilter: 'blur(10px)'
          }}
      >
        <div
          className="d-flex align-items-stretch gap-2"
          style={{
            background: '#ffffff',
            border: '1px solid #d7dee7',
            borderRadius: '16px',
            padding: '6px'
          }}
        >
          <textarea
            ref={inputRef}
            value={inputMessage}
            onChange={handleInputChange}
            onKeyDown={handleKeyPress}
            className="form-control"
            placeholder="Type your message..."
            rows="1"
            style={{
              resize: 'none',
              borderRadius: '12px',
              borderColor: 'transparent',
              padding: '13px 14px',
              boxShadow: 'none',
              minHeight: '48px',
              maxHeight: '110px'
            }}
          />
          <button
            className="btn"
            onClick={() => handleSendMessage().catch((error) => {
              console.error('Send message failed:', error);
            })}
            disabled={!inputMessage.trim() || !currentAgentId}
            style={{
              minWidth: '82px',
              minHeight: '48px',
              borderRadius: '12px',
              background: 'linear-gradient(135deg, #0f766e 0%, #0b8f87 100%)',
              color: '#fff',
              border: 'none',
              fontWeight: 700,
              fontSize: '0.95rem',
              boxShadow: '0 10px 20px rgba(15,118,110,0.2)'
            }}
          >
            Send
          </button>
        </div>
      </div>
    </div>
  );
};

export default ChatWindow;
